import { AppView } from "./event.view.js";

export type NavDirection = "forward" | "back";
export type NavMotion = NavDirection | "fade" | "none" | "travel";

export type NavigateOptions = {
  replace?: boolean;
  motion?: NavMotion;
  subject?: "reading" | "math" | "spelling";
};

const VIEW_DEPTH: Record<AppView, number> = {
  reading: 0,
  settings: 1,
  shop: 1,
  location: 2,
  achievements: 1,
  login: 1,
  "accept-share": 1,
  "add-profile": 2,
  "edit-profile": 2,
  "profile-progress": 3,
  upload: 2,
  instructions: 2,
  "share-profile": 2,
};

const VIEW_RANK: Record<AppView, number> = {
  reading: 0,
  shop: 1,
  location: 2,
  achievements: 3,
  settings: 4,
  login: 5,
  "accept-share": 6,
  "add-profile": 7,
  upload: 8,
  instructions: 9,
  "edit-profile": 10,
  "profile-progress": 11,
  "share-profile": 12,
};

export const viewDepth = (view: AppView): number => VIEW_DEPTH[view];

export const navDirection = (from: AppView, to: AppView): NavDirection => {
  const fromDepth = VIEW_DEPTH[from];
  const toDepth = VIEW_DEPTH[to];
  if (toDepth !== fromDepth) return toDepth > fromDepth ? "forward" : "back";
  return VIEW_RANK[to] >= VIEW_RANK[from] ? "forward" : "back";
};

export const pathForView = (view: AppView, id?: string, subject?: NavigateOptions["subject"]): string => {
  if (view === "add-profile") return "/settings/new";
  if (view === "edit-profile") return `/settings/profile/${id ?? ""}`;
  if (view === "profile-progress") return `/settings/profile/${id ?? ""}/${subject ?? "reading"}`;
  if (view === "upload") return "/settings/upload";
  if (view === "instructions") return "/settings/instructions";
  if (view === "shop") return "/shop";
  if (view === "location") return "/shop/location";
  if (view === "achievements") return "/achievements";
  if (view === "share-profile") return `/settings/share/${id ?? ""}`;
  if (view === "settings") return "/settings";
  if (view === "login") return "/login";
  return "/";
};

export const viewFromPath = (pathname = window.location.pathname): AppView => {
  if (pathname === "/login" || pathname.startsWith("/login/")) return "login";
  if (pathname.startsWith("/share/")) return "accept-share";
  if (pathname.startsWith("/settings/share/")) return "share-profile";
  if (pathname.startsWith("/settings/upload")) return "upload";
  if (pathname.startsWith("/settings/new")) return "add-profile";
  if (pathname.startsWith("/settings/instructions")) return "instructions";
  if (/^\/settings\/profile\/[^/]+\/[^/]+/.test(pathname)) return "profile-progress";
  if (pathname.startsWith("/settings/profile/")) return "edit-profile";
  if (pathname.startsWith("/shop/location")) return "location";
  if (pathname.startsWith("/shop")) return "shop";
  if (pathname.startsWith("/achievements")) return "achievements";
  if (pathname.startsWith("/settings")) return "settings";
  return "reading";
};

export const profileIdFromPath = (pathname = window.location.pathname): string | null => {
  const match = pathname.match(/^\/settings\/profile\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const profileSubjectFromPath = (pathname = window.location.pathname): NavigateOptions["subject"] => {
  const match = pathname.match(/^\/settings\/profile\/[^/]+\/([^/]+)/);
  if (match?.[1] === "math" || match?.[1] === "spelling" || match?.[1] === "reading") return match[1];
  return undefined;
};

export const shareProfileIdFromPath = (pathname = window.location.pathname): string | null => {
  const match = pathname.match(/^\/settings\/share\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const incomingShareFromPath = (pathname = window.location.pathname): { token: string } | null => {
  const match = pathname.match(/^\/share\/([^/]+)$/);
  if (!match?.[1]) return null;
  return { token: decodeURIComponent(match[1]) };
};

let pendingMotion: NavMotion | null = null;

export const consumeNavMotion = (): NavMotion | null => {
  const motion = pendingMotion;
  pendingMotion = null;
  return motion;
};

export const navigate = (view: AppView, id?: string, options: NavigateOptions = {}): void => {
  const path = pathForView(view, id, options.subject);
  pendingMotion = options.motion ?? null;
  const state = { view };
  if (options.replace) {
    window.history.replaceState(state, "", path);
  } else if (window.location.pathname !== path) {
    window.history.pushState(state, "", path);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
};
